import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../../config/paths';
import { useState } from 'react';
import type { FujiyamaDebitBulkInsertTapbotRequest, FujiyamaDebitBulkInsertTapbotResponse } from '@api/v1/admin/tapbot-bulk-insert/type';

export const TapbotBulkInsertPage = () => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<FujiyamaDebitBulkInsertTapbotResponse | null>(null);
    const [formData, setFormData] = useState({
        urls: '',
        level: 3
    });

    if (!user?.has_admin_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    const extractReferralCode = (url: string): string | null => {
        try {
            const parsedUrl = new URL(url);
            return parsedUrl.searchParams.get('start');
        } catch {
            return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!accessToken) {
            setError('認証情報が見つかりません');
            return;
        }

        if (!formData.urls.trim()) {
            setError('紹介リンクを入力してください');
            return;
        }

        setIsLoading(true);
        setError(null);
        setResult(null);

        // 送信時にURLから紹介コードを抽出
        const urls = formData.urls.split('\n').map(url => url.trim()).filter(url => url !== '');
        const codes = urls.map(url => {
            const code = extractReferralCode(url);
            if (!code) {
                throw new Error(`無効な紹介リンク: ${url}`);
            }
            return code;
        });

        try {
            const response = await fetch('/api/v1/admin/tapbot-bulk-insert', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    fujiyamaReferralCodes: codes,
                    level: formData.level
                } as FujiyamaDebitBulkInsertTapbotRequest),
            });

            if (!response.ok) {
                const text = await response.text();
                try {
                    const data = JSON.parse(text);
                    throw new Error(data.message || 'Tapbotの一括登録に失敗しました');
                } catch {
                    throw new Error(text || 'Tapbotの一括登録に失敗しました');
                }
            }

            const result = await response.json() as FujiyamaDebitBulkInsertTapbotResponse;
            setResult(result);
            setFormData({
                urls: '',
                level: 3
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'urls') {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        } else if (name === 'level') {
            setFormData(prev => ({
                ...prev,
                [name]: parseInt(value, 10)
            }));
        }
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-2">Tapbot一括登録</h1>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-700">
                    Tapbotユーザーを一括で登録するツールです。紹介リンクを改行区切りで入力してください。
                </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                {error && (
                    <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                        {error}
                    </div>
                )}

                {result && (
                    <div className="mb-4 space-y-4">
                        {result.processedUsers.length > 0 && (
                            <div className="bg-green-50 p-4 rounded-lg">
                                <h3 className="text-green-700 font-medium mb-2">処理成功</h3>
                                <ul className="list-disc list-inside space-y-1">
                                    {result.processedUsers.map((user, index) => (
                                        <li key={index} className="text-green-600">
                                            {user.fujiyamaReferralCode} (Telegram ID: {user.telegramId})
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {result.errors.length > 0 && (
                            <div className="bg-red-50 p-4 rounded-lg">
                                <h3 className="text-red-700 font-medium mb-2">処理失敗</h3>
                                <ul className="list-disc list-inside space-y-1">
                                    {result.errors.map((error, index) => (
                                        <li key={index} className="text-red-600">
                                            {error.fujiyamaReferralCode}: {error.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="urls" className="block text-sm font-medium text-gray-700 mb-1">
                            紹介リンク（改行区切り）
                        </label>
                        <textarea
                            id="urls"
                            name="urls"
                            value={formData.urls}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            rows={4}
                            placeholder="1行に1つの紹介リンクを入力してください。例:
https://t.me/mountfuji4devstgbot?start=XXXXX
https://t.me/mountfuji4devstgbot?start=YYYYY
https://t.me/mountfuji4devstgbot?start=ZZZZZ"
                        />
                    </div>

                    <div>
                        <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
                            レベル（1-4）（デフォルトは3）
                        </label>
                        <select
                            id="level"
                            name="level"
                            value={formData.level}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            {[1, 2, 3, 4].map(level => (
                                <option key={level} value={level}>レベル {level}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full px-4 py-2 rounded-md ${isLoading
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-indigo-600 hover:bg-indigo-700'
                            } text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                    >
                        {isLoading ? '処理中...' : '一括登録する'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default TapbotBulkInsertPage;